import React, { useState, useContext, useEffect, useRef } from 'react';
import ReAddToCartModal from '@components/ReAddToCartModal';
import ConfirmDialog from '@pages/checkout/ConfirmDialog';
import ItemsUnavailableDialog from '@pages/checkout/ItemsUnavailableDialog';

import CheckoutPage from '@pages/checkout/CheckoutPage';
import { useAuthContext } from '@context/AuthContext';
import { useLambdaFunctionContext, SNAP_ENDPOINTS } from '@context/LambdaFunctionContext';
import { useTokenCredentials } from '@state/useTokenCredentials';
import { useGeneralContext } from '@context/GeneralContext';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import QrCodeDialog from '@pages/checkout/QrCodeDialog';
// TEMPORARILY COMMENTED OUT - FOR FUTURE USE
import { getPriceBySellBand } from '@functions/getItemPriceBySellBand';
import URLS from '@/URLS';
import PaymentDialog from '@pages/checkout/PaymentDialog';
import XenditPaymentDialog from '@pages/checkout/XenditPaymentDialog';
import { v4 as uuidv4 } from 'uuid';
import { sendPostMessageToParent } from '@functions/trafficController';
import { getColorApp } from '@data/localStorageGetter';
import { ObjectId } from 'bson';
import { useStoreAppColor } from '@state/useStoreAppColor';
import { useStoreSiccMember } from '@state/useStoreSiccMember';
import { useStoreCrmMember } from '@state/useStoreCrmMember';
import { useStoreViewBill } from '@state/useStoreViewBill';
import { useStoreFirstCredential } from '@state/useStoreFirstCredential';
import { useStoreOrderCategory } from '@state/useStoreOrderCategory';
import { useStoreSettings } from '@state/useStoreSettings';
import { useStoreOutlet } from '@state/useStoreOutlet';
import { useStoreCartItems } from '@state/useStoreCartItems';
import { useShallow } from 'zustand/react/shallow';
import { getUrlCheckout, getQtyPrepItem } from '@libs/CartContext/helper';
import { resolveUnavailable } from '@libs/CartContext/unavailableItems';
import { flagUnavailableInMenu } from '@libs/CartContext/flagUnavailableInMenu';
import { getItem } from '@libs/CartItem/getItem';
import WeatherWarning from '@pages/checkout/WeatherWarning';
import { useCheckoutState } from '@state/checkout/useCheckoutState';
import useOnlineStatus from '@hooks/useOnlineStatus';
import CheckingSubtotalModal from '@components/CheckingSubtotalModal';
import OvoPaymentModal from '@components/OvoPaymentModal';
import { useStoreSellTime } from '@state/useStoreSellTime';
import { useStoreGenerateKey } from '@state/useStoreGenerateKey';
import useCheckOpenCloseStore from '@hooks/useCheckOpenCloseStore';
import moment from 'moment-timezone';
import { getTimezoneName } from '@libs/getTimeZone';
import { useMiniappConfirm } from '@state/useMiniappConfirm';
import { useMiniappReq } from '@state/useMiniappReq';
import { useStorePayment } from '@state/useStorePaymentMethod';
// const TOKEN_VALIDATOR =
//   import.meta.env[`REACT_APP_VALIDATION_${import.meta.env.REACT_APP_ENV}`];

export const CartContext = React.createContext([{}, () => {}]);

const initialAddToCartModal = {
  visible: false,
  data: null,
};

const initialCheckoutPage = {
  visible: false,
};

const initialConfirmModal = {
  visible: false,
  titleDialog: '',
  messageDialog: '',
  orderMethod: null,
  parameter: null,
};

const initialConfirmPayment = {
  visible: false,
  titleDialog: '',
  messageDialog: '',
  data: null,
};

const initialConfirmPaymentAdyen = {
  visible: false,
  data: null,
};

const initialConfirmPaymentXendit = {
  visible: false,
  data: null,
};

const initialQrcodeModal = {
  visible: false,
  data: null,
};

// Where the container runs. SNAP hand-offs are posted to exactly this origin, never "*",
// because they carry the customer's token.
const CONTAINER_ORIGIN =
  import.meta.env[`VITE_REACT_APP_DOMAIN_CONTAINER_${import.meta.env.MODE}`];

const initialSuggestionPage = {
  visible: false,
};

const initialPaymentResId = {
  trans_id: '',
  miniapp_request_id: '',
  visible: false,
};

const initialWeatherModal = {
  visible: false,
  data: null,
};

const initialSubtotalWarning = {
  visible: false,
};

const initialOvoPaymentModal = {
  visible: false,
};

const initialState = {
  cartModal: initialAddToCartModal,
  checkoutPage: initialCheckoutPage,
  confirmModal: initialConfirmModal,
  confirmPayment: initialConfirmPayment,
  confirmPaymentAdyen: initialConfirmPaymentAdyen,
  confirmPaymentXendit: initialConfirmPaymentXendit,
  qrcodeModal: initialQrcodeModal,
  loadingPayment: false,
  loadingModal: false,
  suggestionPage: initialSuggestionPage,
  isRewardsPage: false,
  confirmExitRewards: false,
  cart: {
    items: [],
  },
  urlXenditPage: null,
  resultCode: '',
  paymentResId: initialPaymentResId,
  weatherWarning: initialWeatherModal,
  serviceCharge: null,
  subtotalWarning: initialSubtotalWarning,
  ovoPaymentModal: initialOvoPaymentModal,
};

const CartProvider = ({ children }) => {
  const [state, setState] = useState(initialState);
  const history = useHistory();
  const { getSetting } = useStoreSettings((state) => ({
    getSetting: state.getSetting,
  }));
  const { getOutletInfo } = useStoreOutlet((state) => ({
    getOutletInfo: state.getOutletInfo,
  }));
  const { hitAPI, getLocalStorage, getTableName, getUserPhone } =
    useAuthContext();
  const {
    openErrorSnackBar,
    getSellBandId,
    showLoading,
    showLostConnectionModal,
  } = useGeneralContext();
  const { t } = useTranslation();

  const {
    hitTransaction,
    isLastTransactionExist,
    getServiceCharge,
    createXenditPaymentLinkAPI,
  } = useLambdaFunctionContext();

  const [, checkOnlineStatus] = useOnlineStatus();
  const [, checkOpenCLose] = useCheckOpenCloseStore();
  const { orderCategory, getCurrentSalesType } = useStoreOrderCategory(
    (state) => ({
      orderCategory: state.orderCategory,
      getCurrentSalesType: state.getCurrentSalesType,
    }),
  );
  const { setSellTimeProducts, setInvalidTimeProducts } = useStoreSellTime(
    (state) => ({
      setSellTimeProducts: state.setSellTimeProducts,
      setInvalidTimeProducts: state.setInvalidTimeProducts,
    }),
  );

  const { isLoggedIn, checkTokenIsExpired } = useStoreFirstCredential(
    (state) => ({
      checkTokenIsExpired: state.checkTokenIsExpired,
      isLoggedIn: state.isLoggedIn,
    }),
  );
  const { credentials } = useStoreFirstCredential((state) => ({
    credentials: state.credentials,
  }));
  const { viewBillId: transId } = useStoreViewBill(
    useShallow((state) => ({
      viewBillId: state.viewBillId,
    })),
  );

  const { appColor } = useStoreAppColor((state) => ({
    appColor: state.appColor,
  }));
  const { siccMember } = useStoreSiccMember((state) => ({
    siccMember: state.siccMember,
  }));
  const { crmMember } = useStoreCrmMember((state) => ({
    crmMember: state.crmMember,
  }));

  const { userUuid, generateUniqueKey } = useStoreGenerateKey((state) => ({
    userUuid: state.userUuid,
    generateUniqueKey: state.generateUniqueKey,
  }));

  const {
    cartItems,
    getCartItems,
    setCartItems,
    setCartItemLocalStorage,
    clearCart,
    getBadgeCount,
    totalPrice,
    changeCartItemQuantity,
    isChooseRewards,
    removeItemInCart,
  } = useStoreCartItems((state) => ({
    cartItems: state.cartItems,
    getCartItems: state.getCartItems,
    setCartItems: state.setCartItems,
    setCartItemLocalStorage: state.setCartItemLocalStorage,
    clearCart: state.clearCart,
    getBadgeCount: state.getBadgeCount,
    totalPrice: state.totalPrice,
    changeCartItemQuantity: state.changeCartItemQuantity,
    isChooseRewards: state.isChooseRewards,
    removeItemInCart: state.removeItemInCart,
  }));

  const { viewBillId, isViewBill, updateIsViewBill, removeIsViewBill } =
    useStoreViewBill((state) => ({
      viewBillId: state.viewBillId,
      isViewBill: state.isViewBill,
      updateIsViewBill: state.updateIsViewBill,
      removeIsViewBill: state.removeIsViewBill,
    }));

  const {
    selectTime: inputSelectTime,
    billGrandTotal,
    billTaxes,
    // billDiscounts
  } = useCheckoutState((state) => ({
    selectTime: state.selectTime,
    billGrandTotal: state.billGrandTotal,
    billTaxes: state.billTaxes,
    // billDiscounts: state.billDiscounts
  }));

  const { getMiniappConfirm, updateMiniappConfirm } = useMiniappConfirm((state) => ({
    getMiniappConfirm: state.getMiniappConfirm,
    updateMiniappConfirm: state.updateMiniappConfirm,
  }));

  const { getMiniappReqId, getMiniappReqXendit } = useMiniappReq((state) => ({
    getMiniappReqId: state.getMiniappReqId,
    getMiniappReqXendit: state.getMiniappReqXendit,
  }));

  const { setSelectedPaymentMethod } = useStorePayment((state) => ({
    setSelectedPaymentMethod: state.setSelectedPaymentMethod,
  }));

  const forceReset = () => {
    setState(initialState);
  };

  const setContextState = (newData) => {
    setState((prev) => ({
      ...prev,
      ...newData,
    }));
  };

  const getContextState = (key) => {
    if (key) {
      return state[key];
    }
    return state;
  };

  const setConfirmModal = (value, orderMethod, parameter) => {
    setContextState({
      confirmModal: {
        visible: value,
        orderMethod: orderMethod,
        parameter: parameter,
      },
    });
  };

  const setConfirmPaymentAdyen = (value, data) => {
    if (data && !data.error) {
      data.color = appColor;
      data.result['env'] = 'TEST';
      data.result['key'] = import.meta.env[
        `VITE_REACT_APP_ADYEN_CLIENT_KEY_${import.meta.env.MODE}`
      ];
      if (
        import.meta.env.MODE === 'PRODUCTION' ||
        import.meta.env.MODE === 'PRODUCTION_B'
      ) {
        data.result['env'] = 'LIVE';
      }
    }
    if (data && !data.error && data.result?.reference) {
      try {
        sessionStorage.setItem('adyenReference', data.result.reference);
      } catch (e) {
        // ignore sessionStorage errors
      }
    }
    setContextState({
      confirmPaymentAdyen: {
        visible: value,
        data: data,
      },
    });
  };

  const setConfirmPaymentXendit = (value, data) => {
    setContextState({
      confirmPaymentXendit: {
        visible: value,
        data: data || null,
      },
    });
  };

  const getResultCode = () => {
    return getContextState('resultCode');
  };

  const getXenditPage = () => {
    const urlXendit = getContextState('urlXenditPage');
    return urlXendit;
  };

  const setCartModal = (value, data) => {
    setContextState({
      cartModal: {
        visible: value,
        data: data || null,
      },
    });
  };

  const setQrcodeModal = (value, data) => {
    setContextState({
      qrcodeModal: {
        visible: value,
        data: data || null,
      },
    });
  };

  const getCartModalData = () => {
    return { ...getContextState('cartModal').data };
  };

  const getCartModalVisibility = () => {
    return getContextState('cartModal').visible;
  };

  const setSuggestionPage = (value) => {
    setContextState({
      suggestionPage: {
        visible: value,
      },
    });
  };

  const getSuggestionPage = () => {
    return getContextState('suggestionPage').visible;
  };

  const setCheckoutPage = (value) => {
    setContextState({
      checkoutPage: {
        visible: value,
      },
    });
  };

  const addToCart = (itemProducts, isUsingMock) => {
    generateUniqueKey();
    const groupId = uuidv4(); // Generate a unique group ID

    // Add groupId only if group.length > 0
    const item =
      itemProducts.group && itemProducts.group.length > 0
        ? { ...itemProducts, isParentGroup: true, groupId }
        : { ...itemProducts, isParentGroup: true };

    let item_plu_name = item.plu_name;
    let localPrepGroup = [];
    let localItem = [];
    delete item.prep_group;
    if (item.promoFrom) {
      localItem.push(...getCartItems());
      const indexOf =
        getCartItems().findIndex((x) => x.timestamp === item.promoFrom) + 1;
      localItem.splice(indexOf, 0, item);
    } else {
      localItem.push(...getCartItems());

      localItem.splice(getCartItems().length, 0, item);
    }

    if (item.is_variant) {
      if (itemProducts.group && itemProducts.group.length > 0) {
        localPrepGroup = [...item.group];
      } else {
        const variantChecked = item.variant_item.find((data) => data.checked);
        if (variantChecked) {
          // add quantity to variant item
          variantChecked.quantity = itemProducts.quantity;
          if (
            variantChecked.prep_group &&
            variantChecked.prep_group.length > 0
          ) {
            localPrepGroup = [...variantChecked.prep_group];
          }
        }
      }
    } else {
      localPrepGroup = [...item.group];
    }

    // CEK DAN FILTER PREP GROUP YANG TERISI SAJA
    if (localPrepGroup && localPrepGroup.length > 0) {
      localPrepGroup
        .map((group) => {
          if (group.prep_item) {
            let localPrepItem = [...group.prep_item.filter((x) => x.checked)];
            group.prep_item = localPrepItem;
            if (group.prep_item.length > 0) {
              return group;
            }
            return null;
          } else {
            let localPrepItem = [
              ...group.item.filter((x) => x.checked && !x.is_product),
            ];
            const uuid_group = group.uuid_group;
            const choose = group.choose;
            group.item.map((x, index) => {
              if (x.checked && x.is_product) {
                x.childGroup = item_plu_name;
                x.parentGroupId = groupId;
                x.signature_token = uuidv4();
                x.timestamp = new Date().getTime() + index + Math.random() * 10;
                // x.quantity = itemProducts.quantity;
                x.uuid_group = uuid_group;
                x.choose = choose;
                if (x.is_variant && x.variant_item.length > 0) {
                  x.variant_item.forEach((variant) => {
                    if (variant.checked) {
                      variant.quantity = itemProducts.quantity;
                    }
                  });
                }
                localItem.push(x);
              }
            });

            let totalQuantityPrepItem = 0;
            if (Array.isArray(localPrepItem)) {
              totalQuantityPrepItem = localPrepItem.reduce(
                (sum, curr) => sum + (curr.quantity || 0),
                0,
              );
            } else if (localPrepItem && typeof localPrepItem === 'object') {
              totalQuantityPrepItem = localPrepItem.quantity || 0;
            }

            group.item = localPrepItem;
            if (group.item.length > 0) {
              return group;
            }
            return null;
          }
        })
        .filter((y) => y);
    }

    if (isUsingMock) {
      // HANYA KETIKA MENGGUNAKAN TOMBOL MOCK DI DEV PANEL
      console.log('dev panel-----');
      setCartItems([...getCartItems(), item]);
    } else {
      // For get Sell Time Data from selected item
      const extractedData = [];

      // Set the current time in the specified offset.
      localItem.forEach((item) => {
        if (item.is_variant && item.variant_item.length > 0) {
          // Check if variant_item exists
          item.variant_item.forEach((variant) => {
            if (variant.checked) {
              // Iterate through variant items
              extractedData.push({
                end_at: variant.end_at,
                sell_end_at: variant.sell_end_at,
                uuid_product: variant.uuid_product,
                is_active: variant.is_active,
              });
            }
          });
        } else {
          if (item.sell_end_at !== undefined || item.end_at !== undefined) {
            extractedData.push({
              end_at: item.end_at,
              sell_end_at: item.sell_end_at,
              uuid_product: item.uuid_product,
              is_active: item.is_active,
            });
          }
        }
      });

      const timezoneName = getTimezoneName(getSetting().timezone); //convert +8 to Asia/Singapore
      const currentMoment = moment.tz(timezoneName); // Get current time in specified timezone
      extractedData.filter((filteredItem) => {
        // Check if the current time is after the sell time.
        const sellEndMoment = moment(filteredItem.sell_end_at, 'HH:mm:ss');

        const endAtMoment = moment.tz(
          filteredItem.end_at,
          'YYYY-MM-DD HH:mm:ss',
          timezoneName,
        );

        if (
          moment().isAfter(sellEndMoment) ||
          currentMoment.isAfter(endAtMoment)
        ) {
          // Set The Invalid Products
          setInvalidTimeProducts(filteredItem);
        }
      });

      setSellTimeProducts(extractedData);
      setCartItems(localItem);
    }

    if (item.is_have_promo) {
      let data = {
        uuid: item.uuid_product,
        stamp: item.timestamp,
        count: item.quantity,
        suggestion: item.is_have_suggestion,
      };
      setContextState({ isRewardsPage: true });
      sendPostMessageToParent('path', URLS.REWARDS);
      history.replace(URLS.REWARDS, data);
    } else if (item.is_have_suggestion) {
      const dataSuggestion = item.uuid_product;
      setSuggestionPage(true);
      sendPostMessageToParent('path', URLS.SUGGESTION);
      history.replace(URLS.SUGGESTION, dataSuggestion);
    }
  };

  // const generateUniqueKey = () => {
  //   const randomPart = Math.random().toString(36).substring(2, 10);
  //   const timestampPart = Date.now().toString(36);
  //   return randomPart + timestampPart;
  // }

  // A refusal from /clientTransaction: the server found a product sold out, or gone from
  // the menu, after this cart was built. Nothing was written, so the whole recovery is in
  // the cart — drop what was refused, keep and order the rest.
  //
  // This lives in the provider rather than inside <ConfirmDialog> because the confirm
  // overlay has to close for the customer to edit their cart, and a dialog mounted inside
  // it cannot outlive that close: it died with its parent, and the terminal ConfirmDialog
  // handlers it fell through to emptied the cart and navigated away.
  // `items` is what the server refused, kept so removal can be recomputed against the
  // cart as it stands; `entries` is that answer mapped onto cart lines for the customer —
  // a refused product inside a package condemns the package, named with its culprit.
  const [unavailable, setUnavailable] = useState({
    open: false,
    items: [],
    entries: [],
  });

  const showUnavailableItems = (items) => {
    const refused = items ?? [];
    setUnavailable({
      open: true,
      items: refused,
      entries: resolveUnavailable(getCartItems(), refused).entries,
    });

    // Draw the refused products — and any package holding one — as sold out in the menu
    // the customer already has, so they cannot add it straight back and be refused again.
    // Patches the getDataAuthStore snapshot in place of re-downloading it.
    flagUnavailableInMenu(refused);

    // Disarm the miniapp confirmation this attempt obtained. <ConfirmDialog> sends as
    // soon as it mounts with the flag already set, and the paths that reopen it from
    // HomePage, SearchProductPage and PaymentDialog do not clear it the way the cart's
    // own button does — leaving it set would send the next order unprompted.
    updateMiniappConfirm(false);

    // Leaves <CheckoutPage> — the cart — mounted underneath.
    handleCloseConfirmModal();
  };

  const hideUnavailableItems = () => {
    setUnavailable({ open: false, items: [], entries: [] });
  };

  const removeUnavailableItems = () => {
    // Against the live cart, not the one that was sent.
    const { keep } = resolveUnavailable(getCartItems(), unavailable.items);

    setCartItems(keep);
    hideUnavailableItems();

    // Everything they had was refused: there is nothing left to confirm, so hand them
    // back to the menu instead of an empty checkout page.
    if (keep.length === 0) {
      setCheckoutPage(false);
    }
  };

  const sendTransaction = async (orderMethod, parameter) => {
    // Every send gets a fresh verdict. The customer can edit the cart and confirm again,
    // and the second refusal may name different products than the first — or none.
    if (unavailable.open) {
      hideUnavailableItems();
    }

    let credential = credentials || {};
    let typesales = orderCategory || {};
    let cartItem = [...getCartItems()];
    let data = {};
    let tokenValidation = {};

    // const uniqueKey = generateUniqueKey();

    //! Block IF 1
    if (Array.isArray(cartItem) && cartItem.length > 0) {
      data = {
        uuid_table: credential.uuid || credential.table,
        parameter: parameter,
        uuid_sales_type: typesales.id,
        order_items: cartItem.map((item) => {
          //TODO:menyusun prep lagi dari service charge
          let prepGroup = [];
          let productId = '';
          let productName = '';
          //cek jika type variant
          if (item.is_variant) {
            const variantChecked = item.variant_item.find(
              (data) => data.checked,
            );
            productId = variantChecked.uuid_product;
            productName = variantChecked.plu_name;
            if (item.group.length > 0) {
              prepGroup = item.group;
            } else {
              if (variantChecked) {
                if (
                  variantChecked.prep_group &&
                  variantChecked.prep_group.length > 0
                ) {
                  prepGroup = [...variantChecked.prep_group];
                }
              }
            }
          } else {
            prepGroup = item.group;
            productId = item.uuid_product;
            productName = item.plu_name;
          }

          // Spread semua prep group kedalam 1 array of prep item
          let selectedPrepItem = [];
          if (prepGroup.length > 0) {
            // SPREAD PREP GROUP NYA
            prepGroup.map((group) => {
              let choiceGroup = group.item ? group.item : group.prep_item;
              selectedPrepItem = [...selectedPrepItem, ...choiceGroup];
              // menghindari error eslint (array.map harus return sesuatu)
              return null;
            });

            // MENGHILANGKAN KEY YANG TIDAK BERGUNA & MANIPULASI QTY
            selectedPrepItem = selectedPrepItem.map((prepItem) => {
              let localPrepItem = { ...prepItem, user_uuid: userUuid };
              delete localPrepItem.checked;
              delete localPrepItem.use_quantity;
              if (orderMethod !== 'waiter_order') {
                delete localPrepItem.price;
              } else {
                localPrepItem['price'] = getPriceBySellBand(
                  localPrepItem,
                  getSellBandId(),
                );
              }
              delete localPrepItem.sell_bands;

              localPrepItem.quantity =
                localPrepItem.quantity !== null && localPrepItem.quantity > 0
                  ? localPrepItem.quantity
                  : 1;
              localPrepItem.product_name = localPrepItem.plu_name;
              return localPrepItem;
            });
          }

          return {
            uuid_product: productId,
            plu_name: item.plu_name,
            product_name: productName,
            quantity: item.quantity,
            note: item.additional_request || [],
            prep_items: selectedPrepItem,
          };
        }),
      };

      // CHECK PRODUCT AND TOKEN
      // tokenValidation = await hitAPI(TOKEN_VALIDATOR, 'POST', credential, data);
      tokenValidation = {
        error: false,
        result: {
          parameter: parameter,
        },
      };
    } else {
      // untuk payment order, jika tidak ada data baru (item product yang dipilih user) maka tidak dilakukan validasi
      // jadi tokenValidation diberi nilai
      tokenValidation.error = false;
    }
    //! Akhir dari Block if 1

    //! Block If 2
    if (tokenValidation) {
      if (tokenValidation.error === false) {
        let typesales = orderCategory || {};
        let outletInfo = getOutletInfo();
        let cartItem = [...getCartItems()];

        let data = {
          // Idempotency key for this submission. Generated once per
          // sendTransaction() call and carried unchanged through the internal
          // retry loop (LambdaFunctionContext.promiseHitTransaction). Lambda upserts on
          // it, so a transport-layer replay or a retried POST resolves to the
          // original transaction instead of creating a duplicate. Intentionally
          // NOT persisted to the cart store — a genuine re-order must mint a
          // fresh key.
          transaction_uuid: uuidv4(),
          table_id: outletInfo.tableId,
          table_name:
            getSetting().customTable === 'custom'
              ? tokenValidation.result.parameter
              : getTableName(),
          sales_type: orderCategory.type_sales_id.toString(),
          sales_name: orderCategory.title.toString(),
          sell_band: typesales.sell_band.toString(),
          no_telp: getUserPhone() || '',
          type_bill: orderMethod,
          order_items: cartItem.map((item) => {
            let prepGroup = [];
            let pluNumber = '';
            let productName = '';
            let _id = item._id;

            if (item.is_variant) {
              const variantChecked = item.variant_item.find(
                (data) => data.checked,
              );
              pluNumber = variantChecked.plu_number;
              productName = variantChecked.plu_name;
              if (item.group.length > 0) {
                prepGroup = item.group;
              } else {
                if (variantChecked) {
                  if (
                    variantChecked.prep_group &&
                    variantChecked.prep_group.length > 0
                  ) {
                    prepGroup = [...variantChecked.prep_group];
                  }
                }
              }
            } else {
              prepGroup = item.group;
              pluNumber = item.plu_number;
              productName = item.plu_name;
            }

            // Spread semua prep group kedalam 1 array of prep item
            let selectedPrepItem = [];
            if (prepGroup.length > 0) {
              // SPREAD PREP GROUP NYA
              prepGroup.map((group) => {
                if (group._id && group._id !== null && group._id !== '') {
                  selectedPrepItem = [...selectedPrepItem, ...group.item];
                } else {
                  selectedPrepItem = [...selectedPrepItem, ...group.prep_item];
                }
                // menghindari error eslint (array.map harus return sesuatu)
                return null;
              });
              // MENGHILANGKAN KEY YANG TIDAK BERGUNA & MANIPULASI QTY
              selectedPrepItem = selectedPrepItem.map((prepItem) => {
                const amount = getPriceBySellBand(prepItem, getSellBandId());
                let localPrepItem = { ...prepItem, user_uuid: userUuid };

                delete localPrepItem.checked;
                delete localPrepItem.use_quantity;
                delete localPrepItem.is_active;
                delete localPrepItem.uuid_product;
                delete localPrepItem._id;
                delete localPrepItem._partition;
                if (orderMethod !== 'waiter_order') {
                  delete localPrepItem.price;
                  delete localPrepItem.sell_bands;
                } else {
                  delete localPrepItem.amount;
                  localPrepItem['price'] = getPriceBySellBand(
                    localPrepItem,
                    getSellBandId(),
                  );

                  delete localPrepItem.sell_bands;
                }

                localPrepItem.quantity =
                  localPrepItem.quantity !== null && localPrepItem.quantity > 0
                    ? getQtyPrepItem(prepItem, item, isViewBill)
                    : 1;

                localPrepItem['amount'] = amount;
                return localPrepItem;
              });
            }

            if (getContextState('serviceCharge')) {
              const prepService = getContextState('serviceCharge');
              let selectedPrepService;
              if (item.is_variant) {
                const itemVariant = getItem(item, 'name');
                selectedPrepService = prepService.find(
                  (service) => itemVariant.uuid_product === service._id,
                );
              } else {
                selectedPrepService = prepService.find(
                  (service) => item.uuid_product === service._id,
                );
              }
              if (selectedPrepService) {
                let dataPrepService = {
                  uuid_sales_type: selectedPrepService.uuid_sales_type,
                  amount: selectedPrepService.amount * item.quantity,
                  plu_name: selectedPrepService.plu_name,
                  plu_number: selectedPrepService.plu_number,
                  title: selectedPrepService.title,
                  quantity: selectedPrepService.quantity * item.quantity,
                  sell_bands: selectedPrepService.sell_bands,
                  user_uuid: userUuid,
                };
                selectedPrepItem.push(dataPrepService);
              }
            }

            let type = '';
            let subProduct = [];

            if (item.is_package || item.type === 'package') {
              type = 'package';
              subProduct = item.sub_product;
            } else if (item.is_variant) {
              type = 'variant';
            } else {
              type = 'product';
            }

            if (orderMethod === 'payment_order') {
              item.signature_token = uuidv4();
            }

            let selectTime = [];
            let requestWithSelectTime = [];
            if (
              !outletInfo.tableId &&
              orderMethod !== 'quick_order' &&
              siccMember &&
              item.isParentGroup
            ) {
              if (
                Array.isArray(item.additional_request) &&
                inputSelectTime !== ''
              ) {
                requestWithSelectTime.push(
                  ...item.additional_request,
                  'TakeAway ' + inputSelectTime,
                );
              } else {
                selectTime.push('TakeAway ' + inputSelectTime);
              }
            } else {
              requestWithSelectTime = item.additional_request;
            }

            const newData = {
              _id: _id,
              plu_number: pluNumber,
              plu_name: productName,
              quantity: item.quantity,
              note: requestWithSelectTime || selectTime,
              prep_items: selectedPrepItem || [],
              type,
              sub_product: subProduct,
              taxes: [],
              signature_token: item.signature_token,
              user_uuid: userUuid,
            };

            if (orderMethod === 'waiter_order') {
              const amount = getPriceBySellBand(item, getSellBandId());
              newData['amount'] = amount;
              newData['sales_name'] = orderCategory.title.toString();
            }

            return newData;
          }),
          miniapp_request_rxdb_id: getMiniappReqId(),
        };

        //custom table rdo atau raptor gak perlu kirim table name dan table id
        if (
          getSetting().customTable === 'rdo' ||
          getSetting().customTable === 'raptor'
        ) {
          delete data.table_id;
          delete data.table_name;
        }

        if (getSetting().customTable === 'custom') {
          delete data.table_id;
        }
        //dipotong 00 dua digit di belakang

        if (siccMember) {
          data.sicc_member = siccMember;

          if (
            getSetting().customTable === 'rdo' ||
            getSetting().customTable === 'raptor'
          ) {
            data.parameter = siccMember.substring(0, siccMember.length - 2);
          } else {
            if (!outletInfo.tableId) {
              data.table_name = 'TA';
              delete data.table_id;
            }
            data.parameter =
              data.table_name +
              '-' +
              siccMember.substring(0, siccMember.length - 2);
            data.table_name =
              data.table_name +
              '-' +
              siccMember.substring(0, siccMember.length - 2);
          }
        }

        // CRM-scan attribution: when the order originated from a CRM "Scan Table"
        // entry, attach the captured member_uid (an id_customer, opaque to us) so
        // FE-Lambda can persist it and BE-Server can forward the gateway-paid order
        // to CRM on bill close. Only attached when actually present + non-empty —
        // a normal (non-CRM) order omits it entirely, and FE-Lambda raises
        // is_order_np only when crm_member is present AND type_bill === 'payment_order'.
        if (crmMember) {
          data.crm_member = {
            member_uid: crmMember,
            scanned_via: 'crm_scan_table',
          };
        }

        // 2. SETELAH DICEK, KIRIM TRANSAKSI SESUAI DENGAN DETAIL DITAHAP 1
        if (orderMethod === 'normal_order' || orderMethod === 'waiter_order') {
          delete data._id; // change untuk mengubah setiap transaksi == transaksi baru

          const miniappAvailable = getMiniappConfirm();
          if (!miniappAvailable) {
            const response = { status: 'error' };
            return JSON.stringify(response);
          }

          let result = await hitTransaction(data);

          if (result?.errorStatus === 'items_unavailable') {
            // Nothing was written, so the cart is still the customer's to fix: close the
            // confirm overlay, name the refused products, and leave them standing on the
            // cart with every line intact. The dialog is owned by this provider, so it
            // survives the close — see showUnavailableItems.
            showUnavailableItems(result.unavailableItems);
            const response = {
              status: 'items_unavailable',
              message: result.errorMessage,
              items: result.unavailableItems ?? [],
            };
            return JSON.stringify(response);
          }

          if (result?.errorMessage) {
            //cari transaksi apakah sudah masuk atau belum. kalo sudah masuk seperti normal,
            //kalo transaksi tidak masuk, munculkan error
            // const transId = localStorage.getItem(localStorageNames.VIEW_BILL_ID);
            let param = {
              table_id: outletInfo.tableId,
              type_bill: orderMethod,
            };

            if (getSetting().customTable !== 'off' && transId) {
              param['_id'] = new ObjectId(transId);
              delete param['table_id'];
            }

            handleCloseConfirmModal();

            const lastTransaction = await isLastTransactionExist(param);
            if (lastTransaction) {
              if (result?.errorStatus !== 'parameter') clearCart();
              const response = { status: 'success' };
              return JSON.stringify(response);
            } else {
              openErrorSnackBar(result?.errorMessage || t('transactionFailed'));
              const response = { status: 'error' };
              return JSON.stringify(response);
            }
          }

          if (result) {
            const response = { status: 'success', trans_id: result.trans_id };
            return JSON.stringify(response);
          } else {
            sendPostMessageToParent('path', URLS.INVALID_TOKEN);
            history.replace(URLS.INVALID_TOKEN);
            const response = { status: 'error' };
            return JSON.stringify(response);
          }
        }
        if (orderMethod === 'payment_order') {
          showLoading(true);
          //is_view_bill set true sebagai tanda jika sudah masuk ke viewbill
          //digunakan untuk handle qty prepitem setelah balik dari viewbill
          /// ini viewbill
          //tidak perlu return karna hit nya beda tempat
          try {
            const miniappAvailable = getMiniappConfirm();
            if (!miniappAvailable) {
              const response = { status: 'error' };
              return JSON.stringify(response);
            }
            let result = await hitTransaction(data);

            if (result?.errorStatus === 'items_unavailable') {
              // This branch has no generic error path: `if (result)` below would treat the
              // refusal as a success and return an undefined trans_id.
              showLoading(false);
              showUnavailableItems(result.unavailableItems);
              const response = {
                status: 'items_unavailable',
                message: result.errorMessage,
                items: result.unavailableItems ?? [],
              };
              return JSON.stringify(response);
            }

            if (result) {
              // Version 5.1.0+ includes miniapp_request_id
              const response = {
                status: 'success',
                trans_id: result.trans_id,
                miniapp_request_id: result.miniapp_request_id,
              };
              return JSON.stringify(response);
            } else {
              showLoading(false);
              sendPostMessageToParent('path', URLS.INVALID_TOKEN);
              history.replace(URLS.INVALID_TOKEN);
              const response = { status: 'error' };
              return JSON.stringify(response);
            }
          } catch (error) {
            console.error('Payment order error:', error);

            // Handle different error types
            if (error.type === 'NETWORK_ERROR') {
              openErrorSnackBar(t('networkError'));
            } else if (error.type === 'TRANSACTION_ERROR') {
              openErrorSnackBar(error.message || t('transactionFailed'));
            } else {
              openErrorSnackBar(error.message || t('unknownError'));
            }
            showLoading(false);
          }
          delete data._id; // change untuk mengubah setiap transaksi == transaksi baru
          updateIsViewBill(true);
        }

        if (orderMethod === 'quick_order') {
          const miniappAvailable = getMiniappConfirm();
          if (!miniappAvailable) {
            const response = { status: 'error' };
            return JSON.stringify(response);
          }
          data = { ...data, parameter: tokenValidation.result.parameter };
          let result = await hitTransaction(data);

          if (result?.errorStatus === 'items_unavailable') {
            // Nothing was written, so the cart is still the customer's to fix: close the
            // confirm overlay, name the refused products, and leave them standing on the
            // cart with every line intact. The dialog is owned by this provider, so it
            // survives the close — see showUnavailableItems.
            showUnavailableItems(result.unavailableItems);
            const response = {
              status: 'items_unavailable',
              message: result.errorMessage,
              items: result.unavailableItems ?? [],
            };
            return JSON.stringify(response);
          }

          if (result?.errorMessage) {
            handleCloseConfirmModal();
            openErrorSnackBar(result?.errorMessage || t('transactionFailed'));
            const response = { status: 'error' };
            return JSON.stringify(response);
          }

          if (result) {
            const response = {
              status: 'success',
              parameter: tokenValidation.result.parameter,
              trans_id: result.trans_id,
            };
            return JSON.stringify(response);
          } else {
            sendPostMessageToParent('path', URLS.INVALID_TOKEN);
            history.replace(URLS.INVALID_TOKEN);
            const response = { status: 'error' };
            return JSON.stringify(response);
          }
        }
      } else {
        if (tokenValidation.status === 401) {
          sendPostMessageToParent('path', URLS.INVALID_TOKEN);
          history.replace(URLS.INVALID_TOKEN);
        } else {
          if (tokenValidation.messages && tokenValidation.messages.length > 0) {
            // JIKA DATA NOT FOUND, MAKA KELUARKAN POP UP DAN FETCH PRODUK TERBARU
            if (tokenValidation.messages[0].includes('data not found')) {
              let productName = tokenValidation.messages[0].split('"');
              openErrorSnackBar(
                t('itemNotFound', {
                  product: productName[1],
                }),
              );
            } else {
              let message;
              tokenValidation.messages.forEach((data) => {
                message = data + ' \n';
              });

              if (message.trim() !== 'Failed to fetch') {
                openErrorSnackBar(message);
              }
            }
          } else {
            openErrorSnackBar('Error something');
          }
        }
        if (orderMethod === 'payment_order') showLoading(false);
        const response = { status: 'error' };
        return JSON.stringify(response);
      }
    } else {
      if (orderMethod === 'payment_order') showLoading(false);
      openErrorSnackBar('Error something');
      const response = { status: 'error' };
      return JSON.stringify(response);
    }
    //! Akhir Block if Ke 2

    // TOKEN MASIH VALID
  };

  /**
   * Create Xendit Payment Link
   * Similar flow to confirmAdyenPayment but for Xendit Payment Links
   */
  const createXenditPaymentLink = async () => {
    showLoading(true);

    try {
      const outletInfo = getOutletInfo();
      const timestamp = Date.now();

      // Get user info (if available)
      const userPhone = getUserPhone();
      const userEmail = siccMember
        ? `${siccMember}@rdo89.com`
        : 'customer@rdo89.com';

      // Prepare cart items
      // Helper to flatten checked items from group/item and return array of their names
      const extractCheckedGroupItemNames = (groupList, category_name) => {
        if (!Array.isArray(groupList)) return [];
        return groupList.flatMap((group) => {
          if (!group || !Array.isArray(group.item)) return [];
          // Only items where checked === true
          return group.item
            .filter((item) => item.checked)
            .map((item) => ({
              name: item.plu_name,
              quantity: item.quantity || 1,
              price: getPriceBySellBand(item, getSellBandId()),
              category: category_name || 'Food & Beverage',
              url: window.location.origin,
            }));
        });
      };

      // Prepare all cart items: main, variant, plus group checked items
      const items = [];

      getCartItems().forEach((item) => {
        // Handle: main product, variant, and group checked
        if (item.is_variant && Array.isArray(item.variant_item)) {
          // Product is a variant type
          const variantChecked = item.variant_item.find((v) => v.checked);
          if (variantChecked) {
            items.push({
              name: variantChecked.plu_name,
              quantity: item.quantity,
              price: getPriceBySellBand(variantChecked, getSellBandId()),
              category: item.category_name || 'Food & Beverage',
              url: window.location.origin,
            });
          } else {
            // If no variant checked, still fallback to main
            items.push({
              name: item.plu_name,
              quantity: item.quantity,
              price: getPriceBySellBand(item, getSellBandId()),
              category: item.category_name || 'Food & Beverage',
              url: window.location.origin,
            });
          }
        } else {
          // Not a variant
          items.push({
            name: item.plu_name,
            quantity: item.quantity,
            price: getPriceBySellBand(item, getSellBandId()),
            category: item.category_name || 'Food & Beverage',
            url: window.location.origin,
          });
        }

        // If it has group (modifier with checked items), collect all checked group items
        if (item.group && Array.isArray(item.group) && item.group.length > 0) {
          const checkedGroupItems = extractCheckedGroupItemNames(
            item.group,
            item.category_name,
          );
          checkedGroupItems.forEach((gi) => items.push(gi));
        }
      });

      // Tambahan: jika ada service charge, push ke items
      const serviceCharge = getContextState('serviceCharge');

      // serviceCharge bisa dalam bentuk array atau object tergantung implementasi backend
      if (serviceCharge) {
        if (Array.isArray(serviceCharge)) {
          // Jika bentuknya array, push semua
          serviceCharge.forEach((sc) => {
            items.push({
              name: sc.plu_name,
              quantity: sc.quantity || 1,
              price: sc.amount || 0,
              category: 'Service Charge',
              url: window.location.origin,
            });
          });
        } else if (typeof serviceCharge === 'object') {
          // Jika single object
          items.push({
            name: serviceCharge.plu_name,
            quantity: serviceCharge.quantity || 1,
            price: serviceCharge.amount || 0,
            category: 'Service Charge',
            url: window.location.origin,
          });
        }
      }

      // Calculate total from billGrandTotal (already includes taxes)
      const typeTax = billTaxes.map((a) => {
        return { type: a.title, value: Math.round(a.value) };
      });
      // const typeDiscount= billDiscounts.map((a) => { return { type : a.discount_name, value: a.amount};})
      const arrayFees = [...typeTax];
      // Prepare request payload
      const payload = {
        external_id: viewBillId,
        reference_id: `${viewBillId}_${timestamp}`,
        amount: billGrandTotal, // Convert number to locale string without grouping
        description: `Payment for ${outletInfo.outletName} - Order #${viewBillId}`,
        invoice_duration: 600, // 24 hours
        currency: 'IDR',
        customer: {
          given_names: siccMember ? `Customer ${siccMember}` : 'Customer',
          email: userEmail,
          mobile_number: userPhone || undefined,
        },
        success_redirect_url: `${
          import.meta.env[
            `VITE_REACT_APP_DOMAIN_CONTAINER_${import.meta.env.MODE}`
          ]
        }/payment-result`,
        failure_redirect_url: `${
          import.meta.env[
            `VITE_REACT_APP_DOMAIN_CONTAINER_${import.meta.env.MODE}`
          ]
        }/payment-result`,
        items: items,
        metadata: {
          trans_id: viewBillId,
          outlet_id: outletInfo.outletId,
          table_name: outletInfo.tableName,
          miniapp_request_id:
            getContextState('paymentResId').miniapp_request_id,
          order_method: getSetting().orderMethod,
        },
        outlet_uuid: outletInfo.outletId,
        fees: arrayFees,
        locale: getOutletInfo().localeCode,
      };

      // Call API via LambdaFunctionContext
      const response = await createXenditPaymentLinkAPI(payload);
      console.log(response);

      showLoading(false);

      if (response && response.success) {
        // Return response for postMessage to container
        return response;
      } else {
        // Handle error
        const errorMessage =
          response?.message ||
          response?.messages?.[0] ||
          'Failed to create payment link';
        openErrorSnackBar(errorMessage);
        return { error: true, message: errorMessage };
      }
    } catch (error) {
      showLoading(false);
      console.error('Error creating Xendit payment link:', error);
      openErrorSnackBar('Failed to create payment link. Please try again.');
      return { error: true, message: error.message };
    }
  };

  // SNAP (OCBC QRIS) is paid on a page of its own: the container opens
  // /check-snapp-payment/:transId in a new tab, and that page creates the QR, counts
  // down, regenerates, cancels and polls the transaction. This app only hands it what it
  // needs and waits for the outcome — the QR, its timers and its polling no longer live
  // here. Same shape as Xendit and Adyen, whose payment UI the container also hosts.
  const pendingSnapTransId = useRef(null);

  const openSnapPaymentTab = () => {
    const outletInfo = getOutletInfo();
    const settings = getSetting() ?? {};
    // The bill's own table: with custom table names it is what the customer entered
    // (e.g. "OP02"), not the table of the scanned QR (outletInfo.tableName, e.g. "Table 1").
    // usePrepareBill sets it from the calculated transaction.
    const billTableName = useCheckoutState.getState().tableName || outletInfo.tableName;
    pendingSnapTransId.current = viewBillId;
    const handoff = {
      transId: viewBillId,
      partition: outletInfo.outletId,
      // Sent exactly as this app sends it to fe-lambda.
      token: useTokenCredentials.getState().getTokenCredentials()?.access_token ?? null,
      api: SNAP_ENDPOINTS,
      payload: {
        outlet_uuid: outletInfo.outletId,
        external_id: viewBillId,
        // No amount: fe-lambda (0.5.29) charges the bill the miniapp calculated, never a client number.
        metadata: {
          trans_id: viewBillId,
          outlet_id: outletInfo.outletId,
          table_name: billTableName,
          miniapp_request_id: getContextState('paymentResId').miniapp_request_id,
          order_method: settings.orderMethod,
        },
      },
      display: {
        outletName: outletInfo.outletName,
        tableName: billTableName ?? settings.tableName ?? null,
        billNumber: viewBillId,
        // Shown until the QR arrives; the page then shows the amount fe-lambda charged.
        amount: billGrandTotal,
        currencySymbol: settings.currencySymbol || settings.currency || 'IDR',
        title: settings.qrcodeTitlePage || '',
        subtitle: t('qrcodeSubTitle'),
        color: {
          primary: appColor?.primaryThemeColor,
          onPrimary: appColor?.secondaryFontColor,
        },
      },
      // Where to land if the browser blocks the new tab and the page opens in this one.
      returnPath: window.location.pathname,
    };
    // Posted at once, nothing awaited: the container opens the tab on receipt, and
    // browsers only allow that close to the customer's tap.
    window.parent.postMessage({ type: 'snap', handoff }, CONTAINER_ORIGIN);
    return { success: true };
  };

  // The bill on screen, readable from the once-registered listener below. After a reload
  // pendingSnapTransId is gone, but the container still delivers the result of the SNAP
  // payment it was waiting on — which is this bill's.
  const currentBillIdRef = useRef(viewBillId);
  currentBillIdRef.current = viewBillId;

  // Ask the bank once (fe-lambda /verifySnapPayment → SNAP proxy) whether the bill was
  // paid: its paid notice occasionally never reaches us. true only when paid.
  const snapPaidAtBank = async (transId) => {
    const url = SNAP_ENDPOINTS.verifySnapPayment;
    if (!url || !transId) return false;
    try {
      const headers = { 'Content-Type': 'application/json' };
      const token = useTokenCredentials.getState().getTokenCredentials()?.access_token;
      if (token) headers.Authorization = token;
      const res = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({ transid: transId, _partition: getOutletInfo().outletId }),
      });
      const result = await res.json();
      return result?.paid === true;
    } catch {
      return false;
    }
  };

  const finishSnapPayment = async (status, transId) => {
    pendingSnapTransId.current = null;
    // 'cancelled' (Cancel, or the payment tab closed): back on the checkout the customer
    // may already have paid with only our notice missing, so check once before staying.
    const paid = status === 'paid' || (status === 'cancelled' && (await snapPaidAtBank(transId)));
    if (paid) {
      clearCart();
      history.replace(URLS.SUCCESS_PAYMENT);
    }
    // Otherwise the customer stays on the bill and can pay again.
  };

  // The payment tab's outcome, relayed by the container.
  useEffect(() => {
    const onMessage = (event) => {
      if (event.origin !== CONTAINER_ORIGIN || event.data?.key !== 'snap-result') return;
      const result = event.data.message;
      const forThisBill =
        result?.transId &&
        (result.transId === pendingSnapTransId.current ||
          result.transId === currentBillIdRef.current);
      if (forThisBill) finishSnapPayment(result.status, result.transId);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
    // eslint-disable-next-line
  }, []);

  // Fallback: the RxDB watch started with the tab (subscribeMiniappRequestSnap) sees the
  // payment too — for when the customer returns to this tab by hand instead.
  useEffect(() => {
    if (getMiniappReqXendit() === 'success' && pendingSnapTransId.current) {
      finishSnapPayment('paid');
    }
    // eslint-disable-next-line
  }, [getMiniappReqXendit()]);

  const clearOrderCart = () => {
    removeIsViewBill();
    // localStorage.removeItem(localStorageNames.IS_VIEW_BILL);
    clearCart();
    setContextState({
      paymentResId: {
        visible: false,
      },
    });
  };

  const handleOpenCheckoutPage = async () => {
    const store = checkOpenCLose(getCurrentSalesType().currentOpen);
    if (store) {
      if (await checkOnlineStatus()) {
        if (getContextState('isRewardsPage')) {
          if (isChooseRewards()) {
            setContextState({ isRewardsPage: false });
            setCheckoutPage(true);
          } else {
            setContextState({ confirmExitRewards: true });
          }
        } else if (getSuggestionPage()) {
          setSuggestionPage(false);
          setCheckoutPage(true);
        } else {
          setCheckoutPage(true);
        }
      } else {
        showLostConnectionModal(true);
      }
    } else {
      history.replace(URLS.OUTLET_CLOSE);
    }
  };

  const handleCloseCheckoutPage = () => {
    setCheckoutPage(false);
    setContextState({
      paymentResId: {
        visible: false,
      },
    });
  };

  const handleOpenConfirmModal = (orderMethod, parameter) => {
    if (checkTokenIsExpired()) {
      sendPostMessageToParent('path', URLS.SESSION_EXPIRED);
      history.replace(URLS.SESSION_EXPIRED);
    } else {
      setConfirmModal(true, orderMethod, parameter);
    }
  };

  const handleCloseConfirmModal = () => {
    setConfirmModal(false, null, null);
  };

  const handleCloseCartModal = (param) => {
    if (!getSetting().isDirectOrder && !getCartModalData().isSuggestionPage) {
      const data = getContextState('cartModal').data;
      const suggestion = data.is_have_suggestion;
      if (suggestion && param !== 'close') {
        setSuggestionPage(true);
      }
    }
    setCartModal(false, null);
  };

  const handleCloseQrcodeModal = () => {
    setQrcodeModal(false, null);
  };

  // const handleModalWeather = (orderMethod, parameter) => {

  //   setContextState({
  //     weatherWarning: {
  //       visible: true,
  //     },
  //     confirmModal: {
  //       orderMethod: orderMethod,
  //       parameter: parameter,
  //     },
  //   });
  // };

  const clickModalWeather = (data) => {
    setContextState({
      weatherWarning: {
        visible: false,
      },
    });
    if (data) {
      if (getContextState('confirmModal').orderMethod !== 'payment_order') {
        handleOpenConfirmModal(
          getContextState('confirmModal').orderMethod,
          getContextState('confirmModal').parameter,
        );
      } else if (
        getContextState('confirmModal').orderMethod === 'payment_order'
      ) {
        sendTransaction(
          getContextState('confirmModal').orderMethod,
          getContextState('confirmModal').parameter,
        );
      }
    } else {
      // ke page lain
      window.top.close();
      console.log('ke page lainn-------');
    }
  };

  const prepServiceCharge = async () => {
    const cartItems = getCartItems();
    const uuidCartItems = cartItems.map((item) => {
      if (item.is_variant) {
        const itemVariant = getItem(item, 'name');
        return itemVariant.uuid_product;
      } else {
        return item.uuid_product;
      }
    });

    const param = {
      uuid_sales_type: getCurrentSalesType().type_sales_uuid,
      uuid_products: uuidCartItems,
    };

    const serviceCharge = await getServiceCharge(param);
    if (serviceCharge && !serviceCharge.error) {
      serviceCharge.result.service_charge.checked = true;
      setContextState({
        serviceCharge: serviceCharge.result.service_charge,
      });
      return serviceCharge.result.service_charge;
    }
    return serviceCharge;
  };

  useEffect(() => {
    forceReset();
    // CEK LOCALSTORAGE KALAU ADA VALUE CART NYA, MAKA MASUKKAN KEDALAM STATE
    let LSCart = getCartItems();
    if (isLoggedIn() && LSCart && LSCart.length > 0) {
      setCartItems(LSCart);
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (getCartItems().length === 0) {
      handleCloseCheckoutPage();
    }
    // KETIKA ADA PERUBAHAN DI STATE, UPDATE JUGA LOCALSTORAGENYA
    setCartItemLocalStorage(getCartItems());
    // eslint-disable-next-line
  }, [cartItems]);

  return (
    <CartContext.Provider
      value={{
        getContextState,
        setContextState,
        setCartModal,
        setQrcodeModal,
        getCartModalVisibility,
        addToCart,
        setCheckoutPage,
        changeCartItemQuantity,
        clearCart,
        removeItemInCart,
        forceReset,
        handleOpenCheckoutPage,
        getSuggestionPage,
        setSuggestionPage,
        clearOrderCart,
        getXenditPage,
        getBadgeCount,
        isLoggedIn,
        totalPrice,
        getCartItems,
        setConfirmPaymentAdyen,
        setConfirmPaymentXendit,
        createXenditPaymentLink,
        openSnapPaymentTab,
        setCartItems,
        getResultCode,
        prepServiceCharge,
      }}>
      {children}

      {getCartModalVisibility() && (
        <ReAddToCartModal
          item={getCartModalData()}
          closeModal={(param) => handleCloseCartModal(param)}
          isSuggestion={getSuggestionPage()}
          dataSuggestion={getContextState('cartModal').data.uuid_product}
          isRewardsPage={getContextState('isRewardsPage')}
        />
      )}

      {getContextState('qrcodeModal').visible && (
        <QrCodeDialog
          closeModal={() => handleCloseQrcodeModal()}
          data={getContextState('qrcodeModal').data}
        />
      )}

      {/* Mounted beside <ConfirmDialog>, not inside it, so a refusal can close the
          confirm overlay and still be read on top of the cart. */}
      <ItemsUnavailableDialog
        open={unavailable.open}
        entries={unavailable.entries}
        onRemove={removeUnavailableItems}
        onClose={hideUnavailableItems}
      />

      {getContextState('checkoutPage').visible && (
        <CheckoutPage
          onViewBill={(orderMethod, parameter) =>
            sendTransaction(orderMethod, parameter)
          }
        />
      )}

      {getContextState('confirmModal').visible && (
        <ConfirmDialog
          closeQRcode={() => forceReset()}
          // resultCode={getResultCode()}
          confirmTransaction={async () => {
            return await sendTransaction(
              getContextState('confirmModal').orderMethod,
              getContextState('confirmModal').parameter,
            );
          }}
        />
      )}

      {getContextState('confirmPaymentAdyen').visible && <PaymentDialog />}
      {getContextState('confirmPaymentXendit').visible && (
        <XenditPaymentDialog />
      )}
      {getContextState('weatherWarning').visible && (
        <WeatherWarning
          actionClick={(value) => clickModalWeather(value)}
          theme={getColorApp()}
        />
      )}
      {getContextState('subtotalWarning').visible && <CheckingSubtotalModal />}
      {getContextState('ovoPaymentModal').visible && (
        <OvoPaymentModal
          open={getContextState('ovoPaymentModal').visible}
          onClose={() => {
            setContextState({ ovoPaymentModal: { visible: false } });
          }}
        />
      )}
    </CartContext.Provider>
  );
};

export const useCartContext = () => {
  const value = useContext(CartContext);
  if (value == null) {
    throw new Error('useCartContext() called outside of a Provider?');
  }
  return value;
};

export default CartProvider;
